import { useMemoizedFn } from 'ahooks';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { MAX_SCALE, MIN_SCALE } from './usePdfViewport';

function getTouchDistance(touches: TouchList): number {
    if (touches.length < 2) return 0;
    const a = touches[0];
    const b = touches[1];
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

export interface UsePinchZoomOptions {
    /** 绑定手势的容器（展示区域） */
    containerRef: RefObject<HTMLElement | null>;
    /** 当前缩放值，捏合开始时作为基准 */
    scale: number;
    /** 应用缩放值，由外部（usePdfViewport.applyScale）提供，走 Tier 机制 */
    applyScale: (scale: number) => void;
    /** 实际执行滚动的容器，用于捏合中心点补偿；未提供时回退到 containerRef */
    scrollContainerRef?: RefObject<HTMLElement | null>;
    /** 是否启用捏合缩放，默认 true */
    enabled?: boolean;
    /** 最小缩放，默认与 usePdfViewport 一致 */
    minScale?: number;
    /** 最大缩放，默认与 usePdfViewport 一致 */
    maxScale?: number;
}

/**
 * 双指捏合缩放 Hook
 *
 * 在指定容器上监听 touch 事件，支持双指捏合放大/缩小。
 * 与 usePdfViewport 的 scale/setScale 配合使用，适用于 PDF 展示区域。
 *
 * - 仅在有双指触摸时生效，单指不拦截（可正常滚动）
 * - 缩放范围由 minScale/maxScale 限制，默认 [0.05, 5]
 * - touchmove 动态注册：仅双指捏合期间挂载 non-passive 监听器，确保 e.preventDefault()
 *   能阻止 WebView 原生页面缩放；单指时无 non-passive 监听器，浏览器可立即滚动，消除卡顿
 * - 使用 requestAnimationFrame 节流，每帧最多一次 setScale，避免卡顿
 * - 与上一帧应用值相同时跳过 setScale，减少边界处的无效渲染
 */
export function usePinchZoom(options: UsePinchZoomOptions): void {
    const {
        containerRef,
        scrollContainerRef,
        scale,
        applyScale,
        enabled = true,
        minScale = MIN_SCALE,
        maxScale = MAX_SCALE,
    } = options;

    const initialDistanceRef = useRef<number>(0);
    const initialScaleRef = useRef<number>(1);
    const isPinchingRef = useRef<boolean>(false);
    const scaleRef = useRef<number>(scale);
    scaleRef.current = scale;
    const rafIdRef = useRef<number>(0);
    const pendingScaleRef = useRef<number | null>(null);
    const lastAppliedScaleRef = useRef<number>(0);
    // 捏合中心点在容器视口中的 Y 坐标（用于滚动补偿）
    const pinchMidYRef = useRef<number>(0);

    const applyScaleStable = useMemoizedFn(applyScale);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        if (enabled === false) return;

        const flushPendingScale = () => {
            rafIdRef.current = 0;
            const pending = pendingScaleRef.current;
            if (pending === null) return;
            pendingScaleRef.current = null;
            if (pending === lastAppliedScaleRef.current) return;
            const prev = lastAppliedScaleRef.current;
            lastAppliedScaleRef.current = pending;
            applyScaleStable(pending);
            // 捏合中心点滚动补偿：scrollTop_new = scrollTop_old + midY × (ratio - 1)
            const scrollEl = scrollContainerRef?.current ?? containerRef.current;
            if (scrollEl && prev > 0) {
                const ratio = pending / prev;
                scrollEl.scrollTop += pinchMidYRef.current * (ratio - 1);
            }
        };

        // non-passive touchmove 仅在双指捏合期间动态挂载/卸载。
        // 原因：passive:false 会让浏览器在每帧 touchmove 时等待 JS 执行完毕才开始滚动，
        // 导致单指滑动时出现卡顿。动态注册确保单指时不存在阻塞监听器，
        // 同时在双指时保留 e.preventDefault() 的能力，阻止 WebView 原生页面缩放。
        const handleTouchMove = (e: TouchEvent) => {
            if (!isPinchingRef.current || e.touches.length !== 2) return;
            e.preventDefault();
            const dist = getTouchDistance(e.touches);
            if (dist <= 0) return;
            const initialDist = initialDistanceRef.current;
            if (initialDist <= 0) return;
            const ratio = dist / initialDist;
            // 阻尼系数 0.6：将手势幅度衰减，避免微小捏合导致过大的缩放跳变
            const next = initialScaleRef.current * (1 + (ratio - 1) * 0.6);
            const clamped = Math.min(maxScale, Math.max(minScale, Number(Number(next).toFixed(2))));
            pendingScaleRef.current = clamped;
            if (rafIdRef.current === 0) {
                rafIdRef.current = requestAnimationFrame(flushPendingScale);
            }
        };

        let pinchMoveRegistered = false;
        const registerPinchMove = () => {
            if (!pinchMoveRegistered) {
                el.addEventListener('touchmove', handleTouchMove, { passive: false });
                pinchMoveRegistered = true;
            }
        };
        const unregisterPinchMove = () => {
            if (pinchMoveRegistered) {
                el.removeEventListener('touchmove', handleTouchMove);
                pinchMoveRegistered = false;
            }
        };

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 2) {
                isPinchingRef.current = false;
                unregisterPinchMove();
                return;
            }
            if (rafIdRef.current !== 0) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = 0;
            }
            pendingScaleRef.current = null;
            isPinchingRef.current = true;
            initialDistanceRef.current = getTouchDistance(e.touches);
            const raw = scaleRef.current;
            const validScale = Number.isFinite(raw) && raw > 0;
            const currentScale = validScale ? raw : 1;
            initialScaleRef.current = Math.min(maxScale, Math.max(minScale, currentScale));
            // 记录捏合中心点 Y（相对于容器顶部）
            const rect = el.getBoundingClientRect();
            const midClientY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            pinchMidYRef.current = midClientY - rect.top;
            // 双指确认后立即注册 non-passive touchmove，确保首帧 e.preventDefault() 生效
            registerPinchMove();
        };

        const endPinch = (e: TouchEvent) => {
            if (e.touches.length < 2) {
                isPinchingRef.current = false;
                unregisterPinchMove();
                if (rafIdRef.current !== 0) {
                    cancelAnimationFrame(rafIdRef.current);
                    rafIdRef.current = 0;
                    flushPendingScale();
                }
            }
        };

        // touchstart/end/cancel 无需 preventDefault，注册为 passive 避免阻塞手势识别
        el.addEventListener('touchstart', handleTouchStart, { passive: true });
        el.addEventListener('touchend', endPinch, { passive: true });
        el.addEventListener('touchcancel', endPinch, { passive: true });

        return () => {
            if (rafIdRef.current !== 0) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = 0;
            }
            el.removeEventListener('touchstart', handleTouchStart);
            unregisterPinchMove();
            el.removeEventListener('touchend', endPinch);
            el.removeEventListener('touchcancel', endPinch);
        };
    }, [enabled, minScale, maxScale, containerRef, scrollContainerRef, applyScaleStable]);
}
